import { useAuth } from '@pooflabs/web';
import { Bot, Cpu, Hexagon, Home, LayoutDashboard, Rocket } from 'lucide-react';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CreditBalancePill } from '@/components/CreditBalance';
import WalletButton from '@/components/WalletButton';

const NAV_ITEMS = [
  { key: 'home', label: 'Home', icon: Home, path: '/' },
  { key: 'agents', label: 'Agents', icon: Bot, path: '/agents' },
  { key: 'gpu-network', label: 'GPU Network', icon: Cpu, path: '/gpu-network' },
  { key: 'rent', label: 'Deploy', icon: Rocket, path: '/rent' },
  { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
] as const;

interface TopNavProps {
  /** 'dark' renders a frosted-glass bar tuned for dark hero/profile screens. */
  variant?: 'light' | 'dark';
}

/**
 * Global navigation. On desktop (lg and up) this renders a single sticky top
 * bar: logo + pill nav links + wallet/credits. Below `lg` (tablet + mobile)
 * the pill nav links don't have room to sit inline with the logo and wallet
 * controls, so they move to a fixed, native-app-style BOTTOM tab bar instead
 * — the top bar shrinks to just logo + wallet/credits, which always fits.
 * This also fixes the tablet-width overflow that used to happen when the
 * pill labels appeared (at the old `sm` breakpoint) alongside a full-width
 * wallet/credits cluster with no room left, causing the sticky header to
 * wrap and visually clip the hero content underneath it.
 */
export const TopNav: React.FC<TopNavProps> = ({ variant = 'light' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const dark = variant === 'dark';

  const isItemActive = (path: string) => (path === '/' ? location.pathname === '/' : location.pathname.startsWith(path));

  return (
    <>
      <header
        className={`sticky top-0 z-50 border-b backdrop-blur-xl transition-colors duration-300 ${
          dark ? 'border-white/10 bg-black/30' : 'border-border/50 bg-background/70'
        }`}
      >
        <div className="container flex items-center justify-between h-16 gap-2 sm:gap-3">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 shrink-0 group"
            aria-label="Go to home"
          >
            <span
              className="flex items-center justify-center w-9 h-9 rounded-xl bg-[#00C805] shadow-sm shadow-[#00C805]/30 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3"
              aria-hidden="true"
            >
              <Hexagon className="w-5 h-5 text-white" strokeWidth={2.5} fill="rgba(255,255,255,0.15)" />
            </span>
            <span className={`font-extrabold text-lg tracking-tight ${dark ? 'text-white' : 'text-foreground'}`}>
              Tensor Hive
            </span>
          </button>

          {/* Pill nav — desktop only. Mobile/tablet get the fixed bottom tab bar below. */}
          <nav
            className={`hidden lg:flex items-center gap-1 rounded-full p-1 border ${
              dark ? 'bg-white/5 border-white/10' : 'bg-secondary/60 border-border/60'
            }`}
          >
            {NAV_ITEMS.map(({ key, label, icon: Icon, path }) => {
              const isActive = isItemActive(path);
              return (
                <button
                  key={key}
                  onClick={() => navigate(path)}
                  aria-label={label}
                  className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold transition-all duration-300 ${
                    isActive
                      ? 'bg-[#00C805] text-white shadow-sm'
                      : dark
                      ? 'text-white/65 hover:text-white hover:bg-white/10'
                      : 'text-muted-foreground hover:text-foreground hover:bg-background/70'
                  }`}
                >
                  <Icon className="w-4 h-4" strokeWidth={2.25} />
                  <span>{label}</span>
                </button>
              );
            })}
          </nav>

          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0 min-w-0">
            {user && (
              <CreditBalancePill
                walletAddress={user.address}
                className={dark ? 'border-white/15 bg-white/5 text-white' : ''}
              />
            )}
            <WalletButton />
          </div>
        </div>
      </header>

      {/* Fixed bottom tab bar — mobile + tablet only (hidden at lg and up). */}
      <nav
        aria-label="Primary"
        className={`lg:hidden fixed bottom-0 inset-x-0 z-50 border-t backdrop-blur-xl pb-[env(safe-area-inset-bottom)] transition-colors duration-300 ${
          dark ? 'border-white/10 bg-black/70' : 'border-border/60 bg-background/90'
        }`}
      >
        <div className="grid grid-cols-5">
          {NAV_ITEMS.map(({ key, label, icon: Icon, path }) => {
            const isActive = isItemActive(path);
            return (
              <button
                key={key}
                onClick={() => navigate(path)}
                aria-label={label}
                aria-current={isActive ? 'page' : undefined}
                className={`flex flex-col items-center justify-center gap-0.5 py-2 transition-colors duration-200 ${
                  isActive
                    ? 'text-[#00C805]'
                    : dark
                    ? 'text-white/55 active:text-white'
                    : 'text-muted-foreground active:text-foreground'
                }`}
              >
                <Icon className="w-5 h-5" strokeWidth={isActive ? 2.5 : 2} />
                <span className="text-[10px] font-semibold leading-none truncate max-w-full px-0.5">{label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
};

export default TopNav;
